// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignInButton from "@plumberssupply/App/Components/Common/SignInButton";
import ProductAddToCartButton from "@plumberssupply/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@plumberssupply/Overrides/Components/ProductQuantityOrdered";
import { color, mergeWithCss, xIsAuthenticated, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    widgetId: string;
    product: ProductModel;
    extendedStyles?: FeaturedProductItemStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
    };
};

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedProductItemStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    actionSection?: InjectableCss;
    addToCartSection?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productOriginallyStyles?: TypographyPresentationProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPriceStyles?: ProductPriceStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    signInButton?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productPreviousPurchase?: TypographyPresentationProps;
}

export const featuredProductItem: FeaturedProductItemStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 0 16px;
            margin-bottom: 16px;
            width: 100%;
            height: 100%;
            ${xScreen("lte", "sm")} {
                max-width: 280px;
                flex-direction: column;
                flex-wrap: nowrap;
                width: auto;
                height: auto;
            }
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 80px;
                height: 80px;
                margin-right: 20px;
                flex: 0 0 80px;
                ${xScreen("lte", "sm")} {
                    flex-basis: auto;
                    margin-left: auto;
                    margin-right: auto;
                    margin-bottom: 10px;
                }
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
                height: auto;
                object-fit: contain;
            `,
        },
    },
    infoSection: {
        css: css`
            max-width: 274px;
            display: flex;
            flex-direction: column;
            flex: 1;
            ${xScreen("lte", "sm")} {
                max-width: none;
            }
        `,
    },
    productBrandStyles: {
        nameLink: {
            typographyProps: {
                size: 10,
                weight: "normal",
                css: css`
                    &&& {
                        line-height: 8px;
                        color: ${color("text")};
                    }
                `,
            },
            css: css`
                margin-bottom: 4px;
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: 12,
                weight: 800,
                css: css`
                    &&& {
                        letter-spacing: 0;
                        line-height: 15px;
                        color: ${color("common.ui11")};
                    }
                `,
            },
            css: css`
                margin-bottom: 8px;
            `,
        },
    },
    productPriceStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                margin-bottom: 16px;
            `,
        },
        price: {
            priceText: {
                size: 14,
                weight: 800,
                css: css`
                    &&&& {
                        letter-spacing: 0;
                        line-height: 18px;
                        margin-right: 8px;
                        color: ${color("common.ui11")};
                    }
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    weight: "normal",
                    size: 14,
                    css: css`
                        &&&& {
                            letter-spacing: 0;
                            line-height: 18px;
                            color: ${color("text")};
                        }
                    `,
                },
            },
        },
    },
    actionSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 100%;
            flex-wrap: wrap;
            justify-content: space-between;
            margin-top: auto;
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 100%;
            margin-bottom: 10px;
        `,
    },
    productAddToCartButtonStyles: {
        mergeCss: true,
        css: css`
            flex-grow: 1;
        `,
    },
    signInButton: {
        mergeCss: true,
        css: css`
            flex-grow: 1;
        `,
    },
    productPreviousPurchase: {
        css: css`
            margin-left: auto;
            line-height: 18px;
        `,
    },
};

const FeaturedProductItem: React.FC<Props> = ({
    product,
    updateProductInfo,
    productInfo,
    extendedStyles,
    widgetId,
}) => {
    const [styles] = useState(() => mergeWithCss(featuredProductItem, extendedStyles));
    const { settingsCollection, isAuthenticated } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
        settingsCollection: getSettingsCollection(state),
    }));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                <StyledWrapper {...styles.infoSection} data-test-selector="HeaderFeaturedProducts-InfoSection">
                    {productContext.product?.brand ? (
                        <ProductBrand brand={productContext.product.brand} extendedStyles={styles.productBrandStyles} />
                    ) : (
                        <StyledWrapper css={styles.productBrandStyles?.nameLink?.css} />
                    )}
                    <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    <ProductPrice
                        product={productContext}
                        extendedStyles={styles.productPriceStyles}
                        showLabel={false}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.actionSection}>
                    <StyledWrapper {...styles.addToCartSection}>
                        {isAuthenticated && productContext.product.canAddToCart ? (
                            <>
                                <ProductQuantityOrdered
                                    labelOverride=""
                                    showActions={true}
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    id={`${productContext.product.id}-qty`}
                                />
                                <ProductAddToCartButton
                                    data-test-selector={`${productContext.product.id}-addToCartBtn`}
                                    extendedStyles={styles.productAddToCartButtonStyles}
                                />
                            </>
                        ) : (
                            <>
                                <SignInButton extendedStyles={styles.signInButton} />
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FeaturedProductItem);
